import React, { useEffect, useRef } from 'react';
import { Box, HStack, Text, IconButton, Badge, Tooltip } from '@chakra-ui/react';
import { FiX, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';

/**
 * ContainerLogTail – renders a live streaming log view for a Kubernetes
 * container.  Read-only; connects to the Django Channels LogTailConsumer.
 *
 * Props:
 *   podName       – K8s pod name
 *   containerName – container inside the pod
 *   onClose       – callback when the user closes the panel
 */
function ContainerLogTail({ podName, containerName, onClose }) {
  const termRef = useRef(null);
  const xtermRef = useRef(null);
  const wsRef = useRef(null);
  const fitAddonRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [connected, setConnected] = React.useState(false);

  useEffect(() => {
    if (!termRef.current) return;

    const term = new Terminal({
      cursorBlink: false,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Menlo, Monaco, 'Courier New', monospace",
      fontSize: 12,
      lineHeight: 1.2,
      theme: {
        background: '#0d1117',
        foreground: '#c9d1d9',
        cursor: '#c9d1d9',
        selectionBackground: '#264f78',
        black: '#484f58',
        red: '#ff7b72',
        green: '#3fb950',
        yellow: '#d29922',
        blue: '#58a6ff',
        magenta: '#bc8cff',
        cyan: '#39c5cf',
        white: '#b1bac4',
        brightBlack: '#6e7681',
        brightRed: '#ffa198',
        brightGreen: '#56d364',
        brightYellow: '#e3b341',
        brightBlue: '#79c0ff',
        brightMagenta: '#d2a8ff',
        brightCyan: '#56d4dd',
        brightWhite: '#f0f6fc',
      },
      scrollback: 10000,
      convertEol: true,
      disableStdin: true,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);
    term.open(termRef.current);

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    setTimeout(() => {
      try { fitAddon.fit(); } catch (e) { /* ignore */ }
    }, 100);

    const token = localStorage.getItem('token');
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${proto}://${window.location.host}/ws/logs/${encodeURIComponent(podName)}/${encodeURIComponent(containerName)}/?token=${token}`;

    term.writeln(`\x1b[90m──── Tailing logs: ${containerName} in ${podName} ────\x1b[0m`);
    term.writeln('');

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);

    ws.onmessage = (event) => {
      term.write(event.data);
    };

    ws.onclose = (event) => {
      setConnected(false);
      if (event.code === 4001) {
        term.writeln('\r\n\x1b[31mAuthentication required. Please log in again.\x1b[0m');
      } else if (event.code === 4003) {
        term.writeln('\r\n\x1b[31mInvalid token. Please log in again.\x1b[0m');
      } else {
        term.writeln('\r\n\x1b[33m──── Stream closed ────\x1b[0m');
      }
    };

    ws.onerror = () => {
      term.writeln('\r\n\x1b[31mWebSocket error. Check your connection.\x1b[0m');
    };

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        try { fitAddon.fit(); } catch (e) { /* ignore */ }
      }, 50);
    });
    resizeObserver.observe(termRef.current);

    return () => {
      resizeObserver.disconnect();
      ws.close();
      term.dispose();
      xtermRef.current = null;
      wsRef.current = null;
      fitAddonRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podName, containerName]);

  // Re-fit on fullscreen toggle
  useEffect(() => {
    setTimeout(() => {
      try { fitAddonRef.current?.fit(); } catch (e) { /* ignore */ }
    }, 150);
  }, [isFullscreen]);

  const containerStyle = isFullscreen
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        bg: '#0d1117',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        bg: '#0d1117',
        borderRadius: 'lg',
        overflow: 'hidden',
        boxShadow: 'dark-lg',
        display: 'flex',
        flexDirection: 'column',
        height: '400px',
      };

  return (
    <Box {...containerStyle}>
      {/* Title bar */}
      <HStack
        bg="#161b22"
        px={3}
        py={1.5}
        spacing={2}
        justify="space-between"
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
        flexShrink={0}
      >
        <HStack spacing={2}>
          <HStack spacing={1.5}>
            <Box w={3} h={3} borderRadius="full" bg="red.400" cursor="pointer" onClick={onClose} />
            <Box w={3} h={3} borderRadius="full" bg="yellow.400" cursor="pointer" onClick={() => setIsFullscreen(f => !f)} />
            <Box w={3} h={3} borderRadius="full" bg="green.400" cursor="pointer" onClick={() => setIsFullscreen(f => !f)} />
          </HStack>
          <Text fontSize="xs" color="whiteAlpha.700" fontFamily="mono" ml={2}>
            logs: {containerName}@{podName}
          </Text>
        </HStack>
        <HStack spacing={1}>
          <Badge
            colorScheme={connected ? 'green' : 'red'}
            fontSize="2xs"
            variant="subtle"
          >
            {connected ? 'STREAMING' : 'DISCONNECTED'}
          </Badge>
          <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton
              icon={isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
              size="xs"
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
              onClick={() => setIsFullscreen(f => !f)}
              aria-label="Toggle fullscreen"
            />
          </Tooltip>
          <Tooltip label="Close log tail">
            <IconButton
              icon={<FiX />}
              size="xs"
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'red.300', bg: 'whiteAlpha.200' }}
              onClick={onClose}
              aria-label="Close log tail"
            />
          </Tooltip>
        </HStack>
      </HStack>

      {/* Log viewport */}
      <Box
        ref={termRef}
        flex="1"
        p={1}
        sx={{
          '& .xterm': { height: '100%' },
          '& .xterm-viewport': { overflow: 'hidden !important' },
        }}
      />
    </Box>
  );
}

export default ContainerLogTail;
